import React, {
    ReactElement,
    ReactNode,
    useEffect,
    useState,
} from "react";

import {
    RiArrowDownSLine, RiArrowLeftSLine, RiArrowRightLine, RiArrowRightSLine,
} from "@remixicon/react";
import classNames from "classnames";
import {
    Dropdown,
} from "react-bootstrap";
// eslint-disable-next-line import/no-extraneous-dependencies
import { DayPickerRangeController, FocusedInputShape } from "react-dates";
// eslint-disable-next-line import/no-extraneous-dependencies
import "react-dates/initialize";

// eslint-disable-next-line import/no-extraneous-dependencies
import moment from "moment";
import { createPortal } from "react-dom";
import Styles from "./DateRangeCalendarPicker.module.scss";

// eslint-disable-next-line import/no-extraneous-dependencies
import "react-dates/lib/css/_datepicker.css";
import { parseFilterDates, tryParseDate } from "../../../date";
import Button from "../../theme/button/Button";
import DropdownList from "../../theme/dropdown/Dropdown";
import { DatePresets } from "../../../__generated__/api";
import { useTheme } from "../../../contexts/Theme";

export interface DatePreferences {
    startDate?: Date;
    endDate?: Date;
    preset?: DatePresets;
}

export interface DateRangeProps {
    value: DatePreferences | undefined;
    className?: string;
    toggleClassName?: string;
    dropdownClassname?: string;
    onChange: (value: DatePreferences) => void;
    icon?: ReactNode;
    toggleText?: string;
    shouldPortal?: boolean;
    shouldShowIcon?: boolean;
}

const PRESETS = [
    { text: "Today", preset: DatePresets.Today },
    { text: "Current Week", preset: DatePresets.CurrentWeek },
    { text: "Last 7 Days", preset: DatePresets.Last_7Days },
    { text: "Last 30 Days", preset: DatePresets.Last_30Days },
    { text: "This Month", preset: DatePresets.ThisMonth },
    { text: "This Year", preset: DatePresets.ThisYear },
];

const MIN_DATE = moment("2020-01-01");
function shortDate(date: Date) {
    return date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "numeric",
    });
}

export function ShortDateRange({ startDate, endDate }: {startDate: Date | undefined, endDate: Date | undefined}) {
    return (
        <>
            {startDate && !endDate && " after "}
            {startDate ? shortDate(startDate) : ""}
            {startDate && endDate && <RiArrowRightLine className="icon-xsmall" />}
            {!startDate && endDate && " before "}
            {endDate ? shortDate(endDate) : ""}
        </>
    );
}
export default function DateRangeCalendarPicker(props: DateRangeProps): ReactElement {
    const { value } = props;
    const [isDisplayDate, setIsDisplayDate] = useState(false);
    const [startDate, setStartDate] = useState<moment.Moment | null>(value?.startDate ? moment(value.startDate) : null);
    const [endDate, setEndDate] = useState<moment.Moment | null>(value?.endDate ? moment(value.endDate) : null);
    const theme = useTheme();
    const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);

    useEffect(() => {
        if (value?.preset) {
            const parsed = parseFilterDates(value);
            setStartDate(parsed.startDate ? moment(parsed.startDate) : null);
            setEndDate(parsed.endDate ? moment(parsed.endDate) : moment(new Date()));
            setFocusedInput("startDate");
        } else {
            if (startDate?.valueOf() !== tryParseDate(value?.startDate)?.getTime()) {
                setStartDate(value?.startDate ? moment(value?.startDate) : null);
            }
            if (endDate?.valueOf() !== tryParseDate(value?.endDate)?.getTime()) {
                setEndDate(value?.endDate ? moment(value?.endDate) : null);
            }
        }
    }, [value?.startDate, value?.endDate, value?.preset]);

    useEffect(() => {
        setFocusedInput("startDate");
    }, [isDisplayDate]);

    const today = new Date();

    const hasSelected = startDate != null || endDate != null || value?.preset != null;

    let menu = (
        <Dropdown.Menu className={classNames(Styles["date-picker"], props.className)}>
            <div className={classNames(
                Styles["date-picker-container"],
                startDate != null ? Styles["start-date-selected"] : undefined,
            )}
            >
                <div className={Styles["date-presets"]}>
                    {PRESETS.map(current => (
                        <Button
                            key={current.preset}
                            variant="ghost-outline-blue"
                            size="small"
                            isActive={value?.preset === current.preset}
                            onClick={evt => {
                                props.onChange({
                                    preset: current.preset,
                                });
                                setIsDisplayDate(false);
                                evt.preventDefault();
                                evt.stopPropagation();
                            }}
                        >
                            {current.text}
                        </Button>

                    ))}
                    <Button
                        variant="ghost-outline-blue"
                        size="small"
                        isActive={!value?.preset}
                        onClick={() => {
                            props.onChange({});
                        }}
                    >
                        Custom Range
                    </Button>
                </div>

                <DayPickerRangeController
                    endDate={endDate}
                    startDate={startDate}
                    onDatesChange={date => {
                        setStartDate(date.startDate);
                        let currentEnd = date.endDate;
                        if (!value?.preset && focusedInput === "endDate") {
                            setEndDate(date.endDate);
                            if (date.endDate != null) {
                                setIsDisplayDate(false);
                            }
                        } else {
                            currentEnd = null;
                            setEndDate(null);
                        }

                        props.onChange({
                            startDate: date.startDate?.toDate(),
                            endDate: currentEnd?.toDate(),
                            preset: undefined,
                        });
                    }}
                    onFocusChange={item => {
                        setFocusedInput(item ?? "startDate");
                    }}
                    minDate={MIN_DATE}
                    maxDate={moment(today)}
                    initialVisibleMonth={null}
                    focusedInput={focusedInput}
                    numberOfMonths={1}
                    enableOutsideDays
                    renderNavNextButton={navProps => (
                        <Button
                            variant="ghost-gray"
                            prefixIcon={<RiArrowRightSLine />}
                            className={classNames(
                                "DayPickerNavigation_button__horizontalDefault",
                                "DayPickerNavigation_rightButton__horizontalDefault",
                            )}
                            {...navProps}
                        />
                    )}
                    renderNavPrevButton={navProps => (
                        <Button
                            variant="ghost-gray"
                            prefixIcon={<RiArrowLeftSLine />}
                            className={classNames(
                                "DayPickerNavigation_button__horizontalDefault",
                                "DayPickerNavigation_leftButton__horizontalDefault",
                            )}
                            {...navProps}
                        />
                    )}
                    hideKeyboardShortcutsPanel
                />
            </div>
        </Dropdown.Menu>
    );
    if (props.shouldPortal) {
        menu = createPortal(menu, document.body);
    }
    return (

        <Dropdown
            className={classNames("p-0 m-0 text-light w-100", props.dropdownClassname)}
            show={isDisplayDate}
            onToggle={() => setIsDisplayDate(curr => !curr)}
        >
            <DropdownList.Toggle
                variant="filter"
                prefixIcon={props.icon}
                className={classNames(
                    theme.isV3 ? "p3x-2 w-100" : "",
                    "text-start d-flex align-items-center",
                    props.toggleClassName,
                )}
                postfixIcon={props.shouldShowIcon ? <RiArrowDownSLine /> : undefined}
                isActive={hasSelected}
            >
                <div className="w-100 d-inline">
                    {props.toggleText && (
                        <>
                            {props.toggleText}
                            :
                            {" "}
                        </>
                    )}

                    {value?.preset ? PRESETS.find(current => current.preset === value.preset)?.text : (
                        <ShortDateRange startDate={startDate?.toDate()} endDate={endDate?.toDate()} />
                    )}
                </div>

            </DropdownList.Toggle>
            {menu}
        </Dropdown>

    );
}

DateRangeCalendarPicker.defaultProps = {

    className: "text-light-400 p-0 me-3",
    dropdownClassname: "",
    toggleClassName: "",
    icon: undefined,
    toggleText: undefined,
    shouldPortal: false,
    shouldShowIcon: true,
};
